/**
 * Custom Slideshow — companion behaviour for sections/custom-slideshow.liquid
 *
 * Uses native CSS scroll-snap for swipe/scroll (touch + trackpad + keyboard
 * all work for free), and an IntersectionObserver to detect which slide is
 * active so the dots / autoplay stay in sync. No external dependencies.
 */
class CustomSlideshow extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-slideshow-track]');
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-dot]'));
    this.nextSectionButton = this.querySelector('[data-scroll-next]');

    this.autoplayDuration = parseInt(this.dataset.autoplay, 10) || 0;
    this.pauseOnVideo = this.dataset.pauseOnVideo === 'true';
    this.currentIndex = 0;
    this.autoplayTimer = null;
    this.isInViewport = true;
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!this.slides.length) return;

    this._observeSlides();
    this._observeViewport();
    this._bindDots();
    this._bindSoundToggles();
    this._bindNextSectionButton();
    this._bindPauseTriggers();

    if (this._shouldAutoplay()) this._startAutoplay();
  }

  disconnectedCallback() {
    this._stopAutoplay();
    this.intersectionObserver?.disconnect();
    this.viewportObserver?.disconnect();
  }

  _shouldAutoplay() {
    if (this.autoplayDuration <= 0 || this.prefersReducedMotion) return false;
    if (!this.isInViewport) return false;
    if (this.pauseOnVideo && this.slides[this.currentIndex]?.hasAttribute('data-video-slide')) return false;
    return true;
  }

  _observeViewport() {
    // Autoplay must never call scrollIntoView() while the slideshow itself
    // is scrolled out of view — otherwise it yanks the page back to show
    // the next slide, which feels like the page "jumping to the top".
    this.viewportObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.isInViewport = entry.isIntersecting;
          if (this.isInViewport && this._shouldAutoplay()) {
            this._startAutoplay();
          } else if (!this.isInViewport) {
            this._stopAutoplay();
          }
        });
      },
      { threshold: 0.25 }
    );
    this.viewportObserver.observe(this);
  }

  _observeSlides() {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
            const index = this.slides.indexOf(entry.target);
            if (index > -1) this._setActiveIndex(index);
          }
        });
      },
      { root: this.track, threshold: [0.6] }
    );
    this.slides.forEach((slide) => this.intersectionObserver.observe(slide));
  }

  _setActiveIndex(index) {
    if (index === this.currentIndex && this.dots[index]?.classList.contains('is-active')) return;
    this.currentIndex = index;

    this.dots.forEach((dot, i) => {
      const isActive = i === index;
      dot.setAttribute('aria-selected', String(isActive));
      dot.classList.toggle('is-active', isActive);
    });

    this._restartDotProgress();

    if (this._shouldAutoplay()) {
      this._startAutoplay();
    } else {
      this._stopAutoplay();
    }
  }

  _bindDots() {
    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this._goTo(i));
    });
  }

  _goTo(index) {
    const slide = this.slides[index];
    if (!slide) return;
    slide.scrollIntoView({ behavior: this.prefersReducedMotion ? 'auto' : 'smooth', inline: 'start', block: 'nearest' });
  }

  _startAutoplay() {
    this._stopAutoplay();
    this._restartDotProgress();
    this.autoplayTimer = setTimeout(() => {
      const nextIndex = (this.currentIndex + 1) % this.slides.length;
      this._goTo(nextIndex);
    }, this.autoplayDuration * 1000);
  }

  _stopAutoplay() {
    if (this.autoplayTimer) {
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  _restartDotProgress() {
    this.dots.forEach((dot) => dot.classList.remove('is-progressing'));
    const activeDot = this.dots[this.currentIndex];
    if (!activeDot || !this._shouldAutoplay()) return;

    activeDot.style.setProperty('--autoplay-duration', `${this.autoplayDuration}s`);
    // Force reflow so the CSS animation restarts from the beginning every time.
    void activeDot.offsetWidth;
    activeDot.classList.add('is-progressing');
  }

  _bindPauseTriggers() {
    this.addEventListener('pointerenter', () => this._stopAutoplay());
    this.addEventListener('pointerleave', () => {
      if (this._shouldAutoplay()) this._startAutoplay();
    });
    this.addEventListener('focusin', () => this._stopAutoplay());
    this.addEventListener('focusout', () => {
      if (this._shouldAutoplay()) this._startAutoplay();
    });
    this.track?.addEventListener('pointerdown', () => this._stopAutoplay());

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this._stopAutoplay();
      } else if (this._shouldAutoplay()) {
        this._startAutoplay();
      }
    });
  }

  _bindSoundToggles() {
    this.querySelectorAll('[data-sound-toggle]').forEach((button) => {
      const media = button.closest('.custom-slideshow__media');
      const video = media?.querySelector('[data-video]');
      if (!video) return;

      button.addEventListener('click', () => {
        video.muted = !video.muted;
        button.setAttribute('aria-pressed', String(!video.muted));
      });
    });
  }

  _bindNextSectionButton() {
    if (!this.nextSectionButton) return;
    this.nextSectionButton.addEventListener('click', () => {
      const section = this.closest('.shopify-section');
      const next = section?.nextElementSibling;
      next?.scrollIntoView({ behavior: this.prefersReducedMotion ? 'auto' : 'smooth' });
    });
  }
}

if (!customElements.get('custom-slideshow')) {
  customElements.define('custom-slideshow', CustomSlideshow);
}